import { isEqual } from 'lodash';
import { type SyntheticEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Descendant } from 'slate';
import { usePlaceImgUrlsForCard } from '../../hooks/trip/trip.hooks';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import useToast from '../../hooks/useToast';
import { useAppDispatch } from '../../redux/hooks';
import { updateCardNotes } from '../../redux/trip/trip.slice';
import { type Card, handleCoverImageChange } from '../../utils/CardDetails';

interface UseCardDetailsModalArgs {
  card: Card;
  tripId: string;
  canEdit: boolean;
  open: boolean;
  isMobile: boolean;
  handleClose?: () => void;
}

export function useCardDetailsModal({
  card,
  tripId,
  canEdit,
  open,
  isMobile,
  handleClose,
}: UseCardDetailsModalArgs) {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notes } = card;
  const [isSlideVisible, setIsSlideVisible] = useState(open);
  const [isModalOpen, setIsModalOpen] = useState(open);
  const [tabValue, setTabValue] = useState(notes ? '1' : '2');
  const [showToolbar, setShowToolbar] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(searchParams.has('photos'));
  const [isEditCoverPhoto, setIsEditCoverPhoto] = useState(false);
  const [isRenamingCard, setIsRenamingCard] = useState(false);
  const { showSnackbarMessage } = useToast();

  // Only the cover resolves on open; the rest wait for the gallery so a modal view is one gateway call.
  const {
    imgUrls,
    isLoading: isLoadingImgUrls,
    onImgError,
  } = usePlaceImgUrlsForCard(tripId, card, open, { coverOnly: !galleryOpen });

  const handleGalleryOpen = () => {
    searchParams.set('photos', 'true');
    setSearchParams(searchParams);
  };

  const handleRename = () => setIsRenamingCard(true);
  const handleRenameComplete = () => setIsRenamingCard(false);

  const handleChangeCoverPhoto = () => {
    setIsEditCoverPhoto(true);
    handleGalleryOpen();
  };

  const handleGalleryClose = () => {
    searchParams.delete('photos');
    setSearchParams(searchParams);
    setIsEditCoverPhoto(false);
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    searchParams.set('card-tab', newValue);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    setTabValue(searchParams.get('card-tab') || tabValue);
    setGalleryOpen(searchParams.has('photos'));
  }, [searchParams, tabValue]);

  // Delays unmounting so the desktop slide animation can finish
  useEffect(() => {
    if (open) {
      setIsModalOpen(true);
      setIsSlideVisible(true);
    } else {
      setIsSlideVisible(false);
    }
  }, [open]);

  const handleCloseModal = () => {
    if (isMobile) {
      handleClose?.();
      return;
    }
    setIsSlideVisible(false);
    setTimeout(() => {
      setIsModalOpen(false);
      handleClose?.();
    }, 100);
  };

  const submitCoverImageChange = (newCoverImage: number) => {
    handleCoverImageChange(
      newCoverImage,
      tripId,
      card.id,
      dispatch,
      handleGalleryClose,
      showSnackbarMessage
    );
  };

  const debouncedSaveNotes = useDebouncedCallback((newNotes: Descendant[]) => {
    if (!isEqual(newNotes, notes)) {
      dispatch(updateCardNotes({ tripId, cardId: card.id, notes: newNotes }));
    }
  }, 500);

  const handleEditorClick = () => {
    if (canEdit) {
      setShowToolbar(true);
    }
  };
  const handleEditorBlur = () => setShowToolbar(false);

  return {
    notes,
    imgUrls,
    isLoadingImgUrls,
    isSlideVisible,
    isModalOpen,
    tabValue,
    showToolbar,
    galleryOpen,
    isEditCoverPhoto,
    isRenamingCard,
    handleGalleryOpen,
    handleRename,
    handleRenameComplete,
    handleChangeCoverPhoto,
    handleGalleryClose,
    handleTabChange,
    handleCloseModal,
    submitCoverImageChange,
    debouncedSaveNotes,
    handleEditorClick,
    handleEditorBlur,
    onImgError,
  };
}
